import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Collapse, FormHelperText, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  StyledMenuItemContainer,
  StyledMenuListItemText,
  StyledSubMenuItemContainer,
} from "../../../components/commonComponents/StyledComponents/styled";
import SelectFieldShell from "../../../components/commonComponents/Selectors/SelectFieldShell";
import useSelectDropdown from "../../../components/commonComponents/Selectors/useSelectDropdown";
import { SELECT_COLOR, SELECTED_OPTION_COLOR, SELECT_MOTION_MS } from "../../../components/commonComponents/Selectors/selectConstants";
import { estimateOptionsContentWidth } from "../../../components/commonComponents/Selectors/selectorOptionUtils";

// Store Owner only. Campaign, clip, and loop keep CompanyHierarchySingleSelect.
// A single tenant root is not a group. The retailers under it are the parents.
const retailerGroupNodes = (retailers) => {
  const tree = Array.isArray(retailers) ? retailers : [];
  if (tree.length === 1 && tree[0]?.type === "root") {
    return Array.isArray(tree[0].children) ? tree[0].children : [];
  }
  return tree;
};

const flattenCompaniesUnder = (nodes, companies) => {
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    if (typeof node === "undefined" || node === null || typeof node.id === "undefined") {
      return;
    }
    if (node.type === "store") {
      companies.push({ id: node.id, label: node.label });
    }
    flattenCompaniesUnder(node.children, companies);
  });
};

// Retailer root is the parent. Only store-level companies under it are choices (branches are skipped).
// A retailer with no stores is left out, because the parent cannot be chosen.
export const buildStoreOwnerGroups = (retailers) => {
  return retailerGroupNodes(retailers)
    .map((retailer) => {
      const companies = [];
      flattenCompaniesUnder(retailer?.children, companies);
      return {
        id: retailer?.id,
        label: retailer?.label,
        companies,
      };
    })
    .filter((group) => typeof group.id !== "undefined" && group.companies.length > 0);
};

const filterGroups = (groups, searchValue) => {
  if (searchValue === "") {
    return groups;
  }
  return groups
    .map((group) => {
      const parentMatches = String(group.label ?? "").toLowerCase().includes(searchValue);
      const companies = parentMatches
        ? group.companies
        : group.companies.filter((company) => String(company.label ?? "").toLowerCase().includes(searchValue));
      if (companies.length === 0) {
        return null;
      }
      return { ...group, companies };
    })
    .filter((group) => group !== null);
};

const StoreOwnerHierarchySelect = ({
  label = "Owner*",
  groups = [],
  value = null,
  onChange,
  fieldError = "",
  elemId = "ownerSelectionForStore",
}) => {
  const [expanded, setExpanded] = useState({});
  const itemRefs = useRef({});
  const dropdown = useSelectDropdown({ instanceId: elemId });
  const { open, setOpen, searchTerm, isFocused, paperRef, close: closeDropdown } = dropdown;

  const close = useCallback(
    (clearSearch = true) => closeDropdown({ clearSearch }),
    [closeDropdown]
  );

  const selectedOption = value && typeof value.id !== "undefined" ? value : null;
  const shrink = isFocused || open || selectedOption !== null;
  const searchValue = searchTerm.trim().toLowerCase();
  const visibleGroups = useMemo(() => filterGroups(groups, searchValue), [groups, searchValue]);

  const contentWidth = useMemo(() => {
    const labels = [];
    groups.forEach((group) => {
      labels.push(group.label);
      group.companies.forEach((company) => labels.push(company.label));
    });
    return estimateOptionsContentWidth(labels, 25);
  }, [groups]);

  // Groups start collapsed. A missing entry means closed, so a retailer that arrives
  // later is closed too. Search ignores this and shows matches either way.
  const isGroupExpanded = (groupId) => searchValue !== "" || expanded[groupId] === true;

  const toggleGroup = (groupId) => {
    const wasExpanded = expanded[groupId] === true;
    setExpanded((current) => ({
      ...current,
      [groupId]: current[groupId] !== true,
    }));

    // Same as the store dropdown: after a group opens, bring its header to the top of the panel.
    if (wasExpanded === true) {
      return;
    }
    setTimeout(() => {
      const item = itemRefs.current[groupId];
      const container = paperRef.current;
      if (!item || !container) {
        return;
      }
      const itemRect = item.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (itemRect.bottom >= containerRect.bottom) {
        const scrollOffset = itemRect.top - containerRect.top - 8;
        container.scrollTo({
          top: container.scrollTop + scrollOffset,
          behavior: "smooth",
        });
      }
    }, 300);
  };

  const handleSelect = (company) => {
    if (typeof onChange === "function") {
      onChange({ id: company.id, label: company.label });
    }
    close(false);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    if (typeof onChange === "function") {
      onChange(null);
    }
  };

  return (
    <SelectFieldShell
      dropdown={dropdown}
      elemId={elemId}
      label={label}
      shrink={shrink}
      enableFullWidth={true}
      showSearch={true}
      canClear={selectedOption !== null}
      onClear={handleClear}
      contentWidth={contentWidth}
      errorContent={
        fieldError ? (
          <FormHelperText sx={{ color: SELECT_COLOR, mt: 0.5 }}>{fieldError}</FormHelperText>
        ) : null
      }
      onAnchorClick={() => setOpen((previous) => !previous)}
      onAnchorKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
        if (event.key === "Escape") {
          close();
        }
      }}
      valueDisplay={
        <Typography
          variant="body2"
          sx={{
            color: SELECT_COLOR,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {selectedOption !== null ? selectedOption.label : ""}
        </Typography>
      }
    >
      {visibleGroups.length === 0 ? (
        <Box sx={{ px: 2, py: 1.5, color: "#62626E", fontSize: 14 }}>No companies found</Box>
      ) : (
        visibleGroups.map((group) => {
          const expandedGroup = isGroupExpanded(group.id);
          return (
            <Box
              key={String(group.id)}
              ref={(element) => {
                itemRefs.current[group.id] = element;
              }}
              sx={{ width: "100%" }}
            >
              <StyledMenuItemContainer
                onClick={() => toggleGroup(group.id)}
                onMouseDown={(event) => event.stopPropagation()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <StyledMenuListItemText
                  primary={group.label}
                  primaryTypographyProps={{ sx: { fontWeight: 700 } }}
                />
                <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                  {expandedGroup ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>
              </StyledMenuItemContainer>
              {/* Same group timing as the other grouped dropdowns. */}
              <Collapse in={expandedGroup} timeout={SELECT_MOTION_MS} unmountOnExit>
                {group.companies.map((company) => {
                  const selected = selectedOption !== null && String(selectedOption.id) === String(company.id);
                  return (
                    <StyledSubMenuItemContainer
                      key={String(company.id)}
                      className="selectorOptions"
                      onClick={() => handleSelect(company)}
                      onMouseDown={(event) => event.stopPropagation()}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: selected === true ? SELECTED_OPTION_COLOR : "transparent",
                      }}
                    >
                      <StyledMenuListItemText
                        primary={company.label}
                        primaryTypographyProps={{
                          sx: {
                            color: selected === true ? SELECT_COLOR : "inherit",
                            fontWeight: selected === true ? 500 : 400,
                          },
                        }}
                      />
                    </StyledSubMenuItemContainer>
                  );
                })}
              </Collapse>
            </Box>
          );
        })
      )}
    </SelectFieldShell>
  );
};

export default StoreOwnerHierarchySelect;
